import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CommissionChart from "./components/CommissionChart";
import { getApiBaseUrl } from "./config";
import { formatCurrency } from "./dashboardUtils";
import { logoutSession } from "./session";
import {
  FaUsers,
  FaFileInvoiceDollar,
  FaRedo,
  FaClock,
  FaChartLine,
  FaMoneyCheckAlt,
  FaUserCircle,
  FaCalendarCheck,
} from "react-icons/fa";
import "./App.css";

const BASE_URL = getApiBaseUrl();

export default function Dashboard() {
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [commissionRate, setCommissionRate] = useState(0);
  const [commissionData, setCommissionData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const handleLogout = () => {
    const email = localStorage.getItem("email");
    logoutSession(email)
      .catch(() => null)
      .finally(() => {
        localStorage.clear();
        navigate("/login");
        window.location.reload();
      });
  };

  const goToProfile = () => navigate("/profile");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${BASE_URL}/api/dashboard`, {
          withCredentials: true,
        });

        if (!res.data || !res.data.agent) {
          throw new Error("Agent not returned in response.");
        }

        const { agent, customers, orders, summary } = res.data;

        setCommissionRate(agent.commission || 0);
        setCustomers(customers || []);
        setOrders(orders || []);
        setSummary(summary || null);
        setCommissionData(summary?.commissionData || []);

        if (agent.name) {
          localStorage.setItem("agent_name", agent.name);
        }
      } catch (err) {
        console.error("Dashboard fetch failed:", err.message);
        setError(err.response?.data?.error || "Could not load dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const metrics = summary?.metrics || {
    customersCount: customers.length,
    confirmedOrdersCount: 0,
    draftOrdersCount: 0,
    draftOrdersAmount: 0,
    todaysOrdersCount: 0,
    todaysOrdersAmount: 0,
    inactiveCustomersCount: 0,
    totalSalesAmount: 0,
    totalRefundAmount: 0,
    netSales: 0,
    amountBasis: "include_vat",
    commissionEarned: 0,
  };
  const amountBasisLabel = metrics.amountBasis === "exclude_vat" ? "Ex VAT" : "Incl. VAT";

  if (loading) {
    return (
      <div className="page-state">
        <div className="spinner" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-state">
        <h2>Dashboard unavailable</h2>
        <p>{error}</p>
        <button className="login-button page-state-action" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  const KPI = ({ title, value, subValue, Icon, color, route }) => {
    const handleClick = () => {
      navigate(route || `/details/${title.replace(/\s+/g, "-").toLowerCase()}`);
    };

    return (
      <div className={`kpi-card kpi-${color}`} onClick={handleClick} style={{ cursor: "pointer" }}>
        <div className="kpi-card-top">
          <div className="kpi-icon-wrap">
            <Icon className="kpi-icon" />
          </div>
          <div className="kpi-arrow">View</div>
        </div>
        <div>
          <div className="kpi-title">{title}</div>
          <div className="kpi-value">{value}</div>
          <div className="kpi-sub">{subValue}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-shell">
      <div className="dashboard-container">
        <div className="dashboard-topbar">
          <div className="profile-menu-container">
            <FaUserCircle className="profile-icon" onClick={() => setDropdownOpen(!dropdownOpen)} />
            {dropdownOpen && (
              <div className="profile-dropdown">
                <div onClick={goToProfile}>Profile</div>
                <div onClick={handleLogout}>Logout</div>
              </div>
            )}
          </div>
        </div>

        {!orders.length && !customers.length ? (
          <div className="page-card empty-state">
            <h2>No dashboard data yet</h2>
            <p>This account is authenticated, but no customers or orders were returned.</p>
          </div>
        ) : null}

        <div className="full-width">
          <div className="section-heading">
            <div>
              <div className="section-title">Headline KPI</div>
              <div className="section-subtitle">Current-month earnings after refunds.</div>
            </div>
          </div>
          <KPI
            title={`Commission Rate: ${commissionRate}%`}
            value={<span className="kpi-value large">{formatCurrency(metrics.commissionEarned)}</span>}
            subValue={`Based on ${amountBasisLabel} sales - refunds`}
            Icon={FaMoneyCheckAlt}
            color="teal"
            route="/details/commission-earned"
          />
        </div>

        <div className="section-heading">
          <div>
            <div className="section-title">Operational Snapshot</div>
            <div className="section-subtitle">Tap a card to open the detailed view.</div>
          </div>
        </div>

        <section className="kpi-chart-grid">
          <div className="kpi-left">
            <KPI
              title="Today's Orders"
              value={formatCurrency(metrics.todaysOrdersAmount)}
              subValue={`${metrics.todaysOrdersCount} Orders Today • ${amountBasisLabel}`}
              Icon={FaCalendarCheck}
              color="pink"
              route="/details/todays-orders"
            />

            <KPI
              title="Total Sales"
              value={formatCurrency(metrics.totalSalesAmount)}
              subValue={`${metrics.confirmedOrdersCount} Orders • ${amountBasisLabel}`}
              Icon={FaFileInvoiceDollar}
              color="blue"
            />

            <div className="kpi-row">
              <KPI
                title="Net Sales"
                value={formatCurrency(metrics.netSales)}
                subValue={`Total Confirmed Sales • ${amountBasisLabel}`}
                Icon={FaChartLine}
                color="green"
              />
              <KPI
                title="Customers"
                value={metrics.customersCount}
                subValue="Total customers"
                Icon={FaUsers}
                color="purple"
              />
              <KPI
                title="Inactive Customers"
                value={metrics.inactiveCustomersCount}
                subValue="5+ days no trade"
                Icon={FaClock}
                color="gray"
              />
            </div>
          </div>

          <div className="kpi-middle">
            <KPI
              title="Refunds"
              value={formatCurrency(metrics.totalRefundAmount)}
              subValue={`Current month refunds • ${amountBasisLabel}`}
              Icon={FaRedo}
              color="red"
            />
            <KPI
              title="Draft Orders"
              value={formatCurrency(metrics.draftOrdersAmount)}
              subValue={`${metrics.draftOrdersCount} Drafts • ${amountBasisLabel}`}
              Icon={FaClock}
              color="orange"
            />
          </div>
        </section>

        <section className="dashboard-chart-section">
          <div className="section-heading">
            <div>
              <div className="section-title">Commission Trend</div>
              <div className="section-subtitle">Monthly commission performance in its own section.</div>
            </div>
          </div>

          <div className="dashboard-card chart-column">
            <div style={{ width: "100%", maxWidth: "100%" }}>
              <CommissionChart commissionData={commissionData} />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
